import type { ScoringRules } from "@/features/predictions/types/competition.type";

export const SCORING_RULES = {
  exactScore: 5,
  correctResultAndDiff: 3,
  correctWinner: 2,
  correctQualifier: 1,
} as const;

export type ScoringExampleRow = {
  id: string
  match: string
  prediction: string
  detail: string
  points: string
};

export const SCORING_EXAMPLES: readonly ScoringExampleRow[] = [
  {
    id: "ex-1",
    match: "Paris 2-1 Milan",
    prediction: "2-1 Paris",
    detail: "Exact et qualifié",
    points: "5 + 1 = 6",
  },
  {
    id: "ex-2",
    match: "Paris 2-1 Milan",
    prediction: "3-2 Paris",
    detail: "Bon résultat, bonne diff. (1) et qualifié",
    points: "3 + 1 = 4",
  },
  {
    id: "ex-3",
    match: "Paris 2-1 Milan",
    prediction: "4-1 Paris",
    detail: "Bon résultat et qualifié",
    points: "2 + 1 = 3",
  },
  {
    id: "ex-4",
    match: "Paris 2-1 Milan",
    prediction: "1-2 Milan",
    detail: "Mauvais résultat",
    points: "0 + 0 = 0",
  },
  {
    id: "ex-nul",
    match: "Paris 1-1 Milan",
    prediction: "2-2",
    detail: "Bon résultat (nul) sans le score exact*",
    points: "2",
  },
  {
    id: "ex-5",
    match: "Paris 1-1 Milan (Milan TAB)",
    prediction: "1-1 Milan",
    detail: "Exact et qualifié",
    points: "5 + 1 = 6",
  },
  {
    id: "ex-6",
    match: "Paris 1-1 Milan (Milan TAB)",
    prediction: "1-1 Paris",
    detail: "Exact et mauvais qualifié",
    points: "5 + 0 = 5",
  },
  {
    id: "ex-7",
    match: "Paris 2-2 Milan (Milan TAB)",
    prediction: "3-3 Milan",
    detail: "Bon résultat (nul) et qualifié",
    points: "2 + 1 = 3",
  },
] as const;

// Last-resort content used while the API does not serve the competition's
// scoring_rules field (deploy window, stale caches). Same shape and wording
// as what the API's ScoringRulesContent builder produces.
export const DEFAULT_SCORING_RULES: ScoringRules = {
  version: 1,
  rules: [
    {
      key: "exact_score",
      category: "base",
      label: "Score exact",
      description: "Tu pronostiques le score exact du match.",
      points: SCORING_RULES.exactScore,
    },
    {
      key: "result_and_diff",
      category: "base",
      label: "Bon résultat avec bonne différence",
      description: "Tu trouves le bon vainqueur et le bon écart de buts, sans le score exact.*",
      points: SCORING_RULES.correctResultAndDiff,
    },
    {
      key: "result_only",
      category: "base",
      label: "Bon résultat seul",
      description: "Tu trouves le vainqueur (ou le match nul) sans le bon score ni la bonne différence.",
      points: SCORING_RULES.correctWinner,
    },
    {
      key: "correct_qualifier",
      category: "bonus",
      label: "Qualifié trouvé (élimination directe)",
      description: "Bonus sur les matchs à élimination directe : tu trouves l'équipe qui passe au tour suivant.",
      points: SCORING_RULES.correctQualifier,
    },
    {
      key: "tiebreaker",
      category: "tiebreaker",
      label: "Premier arrivé, premier classé",
      description: "À points égaux, le joueur ayant enregistré son pronostic le plus tôt prend l'avantage.",
    },
  ],
  footnotes: [
    "Mauvais résultat ou pas de pronostic : 0 pt. Les points sont calculés au coup de sifflet final.",
    "* hors matchs nuls",
  ],
  examples: SCORING_EXAMPLES.map(({
    match, prediction, detail, points,
  }) => ({
    match, prediction, detail, points,
  })),
};
